import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Details, Heading, Paragraph, Select, Table, ValidationMessage } from '@digdir/designsystemet-react';
import type { RiskMatrixFactor, RiskMatrixSettings } from '../../../../api/hms';
import { CONSEQUENCE_LEVELS, consequenceOption } from '../../hmsFormat';
import {
  consequenceOf,
  FIELD_IDS,
  oneReportConsequenceError,
  tooLowForOneReport,
  withConsequence,
} from '../riskMatrixView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskMatrix.module.css';

type Props = {
  settings: RiskMatrixSettings;
  factors: RiskMatrixFactor[];
  onChange: (next: RiskMatrixSettings) => void;
};

export const ConsequenceCard = ({ settings, factors, onChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const tooLow = tooLowForOneReport(settings, factors).map((factor) => factor.key);

  return (
    <section aria-labelledby="hr-rm-consequence" className={layout.card} data-gap="md">
      <div className={layout.cardHeading}>
        <span className={layout.step}>{t('hrRiskMatrix.step', { step: 3, total: 4 })}</span>
        <Heading level={2} data-size="sm" id="hr-rm-consequence">
          {t('hrRiskMatrix.consequence.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t('hrRiskMatrix.consequence.intro')}
        </Paragraph>
      </div>
      <Details>
        <Details.Summary>{t('hrRiskMatrix.consequence.levelsTitle')}</Details.Summary>
        <Details.Content>
          <dl className={classes.levelList}>
            {CONSEQUENCE_LEVELS.map((level) => (
              <div key={level} className={classes.levelItem}>
                <dt className="ds-label" data-size="sm">
                  {consequenceOption(level, t)}
                </dt>
                <dd className="ds-paragraph" data-size="sm">
                  {t(`hms.consequenceDescriptions.${level}`)}
                </dd>
              </div>
            ))}
          </dl>
        </Details.Content>
      </Details>
      <div className={layout.tableScroll}>
        <Table data-size="sm" className={classes.consequenceTable}>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>{t('hrRiskMatrix.consequence.area')}</Table.HeaderCell>
              <Table.HeaderCell className={classes.consequenceColumn}>
                {t('hrRiskMatrix.consequence.consequence')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {factors.map((factor) => {
              const value = consequenceOf(settings, factor);
              const changed = value !== factor.standardConsequence;
              // One report gives high risk, so its cell must be high too. Shown as HR chooses.
              const error = tooLow.includes(factor.key) ? oneReportConsequenceError(factor.name, settings, t) : null;
              const errorId = `${FIELD_IDS.consequence(factor.key)}-error`;
              return (
                <Table.Row key={factor.key}>
                  <Table.HeaderCell scope="row" className={layout.rowHeader}>
                    {factor.name}
                    {changed && (
                      <span className={classes.changed}>
                        {t('hrRiskMatrix.consequence.changedFromStandard', {
                          standard: consequenceOption(factor.standardConsequence, t),
                        })}
                      </span>
                    )}
                  </Table.HeaderCell>
                  <Table.Cell>
                    <Select
                      id={FIELD_IDS.consequence(factor.key)}
                      data-size="sm"
                      aria-label={t('hrRiskMatrix.consequence.selectLabel', { area: factor.name })}
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? errorId : undefined}
                      value={value}
                      onChange={(event) => onChange(withConsequence(settings, factor, Number(event.target.value)))}
                    >
                      {CONSEQUENCE_LEVELS.map((level) => (
                        <Select.Option key={level} value={level}>
                          {consequenceOption(level, t)}
                        </Select.Option>
                      ))}
                    </Select>
                    {error && (
                      <ValidationMessage id={errorId} data-size="sm">
                        {error}
                      </ValidationMessage>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
      <Paragraph data-size="xs" className={layout.subtle}>
        {t('hrRiskMatrix.consequence.footnote')}
      </Paragraph>
    </section>
  );
};
